import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import Autosuggest from 'react-autosuggest';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './RoutingGuide.scss';

const EditRouteForm = ({ setSuccessMessage, fetchRoutes }) => {
  const { routing_guide_id } = useParams();
  const [searchParams] = useSearchParams();
  const providerType = searchParams.get('providerType');

  const [editRoute, setEditRoute] = useState({
    origin: '',
    destination: '',
    provider: '',
    lcl_booking_closing_cutoff_offset_days: -1,
    lcl_cargo_closing_cutoff_offset_days: 0,
    lcl_documentation_closing_cutoff_offset_days: -1
  });

  const [originPortId, setOriginPortId] = useState('');
  const [destinationPortId, setDestinationPortId] = useState('');
  const [originPortCode, setOriginPortCode] = useState('');
  const [destinationPortCode, setDestinationPortCode] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [providers, setProviders] = useState([]);
  const [error, setError] = useState('');
  const [affectedSchedules, setAffectedSchedules] = useState('Scanning...');
  const [checkingPair, setCheckingPair] = useState(false);
  const [portPairExists, setPortPairExists] = useState(false);
  const [portPairError, setPortPairError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRoute = async () => {
      try {

        const response = await axios.get(`/api/routes/${routing_guide_id}`);
        const route = response.data;
        setEditRoute({
          origin: route.origin_port_code || '',
          destination: route.destination_port_code || '',
          provider: route.provider_id,
          lcl_booking_closing_cutoff_offset_days: route.lcl_booking_closing_cutoff_offset_days || 0,
          lcl_cargo_closing_cutoff_offset_days: route.lcl_cargo_closing_cutoff_offset_days || 0,
          lcl_documentation_closing_cutoff_offset_days: route.lcl_documentation_closing_cutoff_offset_days || 0
        });
        setOriginPortId(route.origin_port_id || '');
        setDestinationPortId(route.destination_port_id || '');
        setOriginPortCode(route.origin_port_code || '');
        setDestinationPortCode(route.destination_port_code || '');
        // fetchOriginPortName(route.origin_port_id);
        // fetchDestinationPortName(route.destination_port_id);
      } catch (error) {
        console.error('Error fetching route:', error);
      }
    };

    const fetchProviders = async () => {
      try {
        const response = await axios.get('/api/providers', {
          params: { providerType },
        });
        setProviders(response.data);
      } catch (error) {
        console.error('Error fetching providers:', error);
      }
    };

    // Update fetchAffectedSchedules to parse matched_schedules as a number
    const fetchAffectedSchedules = async () => {
      try {
        const response = await axios.get(`/api/routes/${routing_guide_id}/match_schedule_count`);
        const matchedSchedules = parseInt(response.data.matched_schedules, 10);
        setAffectedSchedules(!isNaN(matchedSchedules) ? matchedSchedules : 0);
      } catch (error) {
        console.error('Error fetching affected schedules:', error);
        setAffectedSchedules('Error');
      }
    };

    fetchRoute();
    if (providerType) {
      fetchProviders();
    }
    fetchAffectedSchedules();
  }, [routing_guide_id, providerType]);

  // Update fetchPortSuggestions to expect only JSON responses
  const fetchPortSuggestions = async (value, setSuggestions) => {
    try {
      const response = await axios.get('/api/port-names-by-uncode-or-name', { 
        params: { query: value },
        responseType: 'json' // Ensure response is parsed as JSON
      });
      
      // Verify Content-Type header
      if (response.headers['content-type'].includes('application/json')) {
        setSuggestions(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching port suggestions:', error);
      // Optionally set an error state here
    }
  };

  const fetchOriginPortName = async (portId) => {
    try {
      const response = await axios.get(`/api/port_name/${portId}`);
      // if (response.data.port_name) {
      //   setOriginPortCode(response.data.port_name);
      // }
    } catch (error) {
      console.error('Error fetching origin port name:', error);
    }
  };

  const fetchDestinationPortName = async (portId) => {
    try {
      const response = await axios.get(`/api/port_name/${portId}`);
      // if (response.data.port_name) {
      //   setDestinationPortCode(response.data.port_name);
      // }
    } catch (error) {
      console.error('Error fetching destination port name:', error);
    }
  };

  const checkPortPair = async (newOrigin, newDestination, newProvider) => {
    if (!newOrigin || !newDestination || !newProvider) {
      setPortPairExists(false);
      setPortPairError('');
      return;
    }
  
    setCheckingPair(true);
    setPortPairExists(false);
    setPortPairError('');
    try {
      console.log("Checking port pair", newOrigin, newDestination, newProvider);
      const response = await axios.get(`/api/routes/${routing_guide_id}/check-exists`, {
        params: {
          origin: newOrigin,
          destination: newDestination,
          provider: newProvider
        }
      });
      if (response.data.exists) {
        setPortPairExists(true);
        setPortPairError('The port pair already exists for this LCL Schedule Provider.');
      } else {
        setPortPairExists(false);
        setPortPairError('');
      }
    } catch (error) {
      console.error('Error checking port pair:', error);
    } finally {
      setCheckingPair(false);
    }
  };

  const handleSaveEditRoute = async () => {
    const {
      provider,
      lcl_booking_closing_cutoff_offset_days,
      lcl_cargo_closing_cutoff_offset_days,
      lcl_documentation_closing_cutoff_offset_days
    } = editRoute;

    if (!originPortId || !destinationPortId || !provider) {
      setError('All fields are required.');
      return;
    }

    const data = {
      origin: originPortCode,
      destination: destinationPortCode,
      provider,
      lcl_booking_closing_cutoff_offset_days,
      lcl_cargo_closing_cutoff_offset_days,
      lcl_documentation_closing_cutoff_offset_days
    };

    try {
      const response = await axios.put(`/api/routes/${routing_guide_id}`, data);
      if (response.status === 200) {
        setSuccessMessage('Route was successfully updated.');
        setError('');
        setTimeout(() => {
          setSuccessMessage('');
        }, 10000);
        fetchRoutes();
        navigate(`/routing-guide/list?providerType=${providerType}`); // Redirect to the Routing Guide listing page
      }
    } catch (error) {
      setError('Error updating route.');
      console.error('Error updating route:', error);
    }
  };

  const inputProps = (name, value, onChange) => ({
    placeholder: `Enter ${name} code`,
    value,
    onChange
  });

  const handleCancel = () => {
    navigate(`/routing-guide/list?providerType=${providerType}`);
  };

  return (
    <div>
      <Button variant="secondary" onClick={handleCancel} className="mb-3">
        Back
      </Button>
      <h1>Edit Route</h1>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <div>
          <strong>Affected Schedules: </strong>
          {typeof affectedSchedules === 'number' ? affectedSchedules : affectedSchedules}
          {typeof affectedSchedules === 'string' && affectedSchedules === 'Scanning...' && (
            <Spinner animation="border" size="sm" />
          )}
        </div>
      </div>
      <div className="form-section">
        <Form.Group controlId="origin">
          <Form.Label>Origin</Form.Label>
          <div style={{ position: 'relative' }}>
            <Autosuggest
              suggestions={originSuggestions}
              onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setOriginSuggestions)}
              onSuggestionsClearRequested={() => setOriginSuggestions([])}
              getSuggestionValue={suggestion => suggestion.port_code}
              renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
              inputProps={{
                ...inputProps('origin', originPortCode, (e, { newValue }) => {
                  setOriginPortCode(newValue);
                  checkPortPair(newValue, destinationPortCode, editRoute.provider);
                }),
                required: true,
                readOnly: true, // Made read-only
              }}
              onSuggestionSelected={(e, { suggestion }) => setOriginPortCode(suggestion.port_code)}
              theme={{
                container: 'react-autosuggest__container',
                input: 'react-autosuggest__input',
                suggestionsContainer: 'react-autosuggest__suggestions-container',
                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                suggestionsList: 'react-autosuggest__suggestions-list',
                suggestion: 'react-autosuggest__suggestion',
                suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
              }}
            />
            {checkingPair && (
              <Spinner
                animation="border"
                size="sm"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              />
            )}
            {!checkingPair && originPortCode && destinationPortCode && (
              portPairExists ? (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'red' }}>✗</span>
              ) : (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'green' }}>✓</span>
              )
            )}
          </div>
          {portPairError && <div style={{ color: 'red' }}>{portPairError}</div>}
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="destination">
          <Form.Label>Destination</Form.Label>
          <div style={{ position: 'relative' }}>
            <Autosuggest
              suggestions={destinationSuggestions}
              onSuggestionsFetchRequested={({ value }) => fetchPortSuggestions(value, setDestinationSuggestions)}
              onSuggestionsClearRequested={() => setDestinationSuggestions([])}
              getSuggestionValue={suggestion => suggestion.port_code}
              renderSuggestion={suggestion => <div>{suggestion.port_code} {suggestion.port_name ? ` (${suggestion.port_name})` : ''}</div>}
              inputProps={{
                ...inputProps('destination', destinationPortCode, (e, { newValue }) => {
                  setDestinationPortCode(newValue);
                  checkPortPair(originPortCode, newValue, editRoute.provider);
                }),
                required: true,
                readOnly: true, // Made read-only
              }}
              onSuggestionSelected={(e, { suggestion }) => setDestinationPortCode(suggestion.port_code)}
              theme={{
                container: 'react-autosuggest__container',
                input: 'react-autosuggest__input',
                suggestionsContainer: 'react-autosuggest__suggestions-container',
                suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                suggestionsList: 'react-autosuggest__suggestions-list',
                suggestion: 'react-autosuggest__suggestion',
                suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
              }}
            />
            {checkingPair && (
              <Spinner
                animation="border"
                size="sm"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              />
            )}
            {!checkingPair && originPortCode && destinationPortCode && (
              portPairExists ? (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'red' }}>✗</span>
              ) : (
                <span style={{ position: 'absolute', top: '10px', right: '10px', color: 'green' }}>✓</span>
              )
            )}
          </div>
          {portPairError && <div style={{ color: 'red' }}>{portPairError}</div>}
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="provider">
          <Form.Label>Provider</Form.Label>
          <Form.Control
            as="select"
            name="provider"
            value={editRoute.provider}
            onChange={(e) => {
              const newProvider = e.target.value;
              setEditRoute({ ...editRoute, provider: newProvider });
              if (originPortCode && destinationPortCode && newProvider) {
                checkPortPair(originPortCode, destinationPortCode, newProvider);
              }
            }}
          >
            <option value="">Select...</option>
            {providers.map((provider) => (
              <option key={provider.provider_id} value={provider.provider_id}>
                {provider.provider_name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_booking_closing_cutoff_offset_days">
          <Form.Label>Booking Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={editRoute.lcl_booking_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setEditRoute({ ...editRoute, lcl_booking_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Booking</small>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_cargo_closing_cutoff_offset_days">
          <Form.Label>Cargo Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={editRoute.lcl_cargo_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setEditRoute({ ...editRoute, lcl_cargo_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Cargo</small>
        </Form.Group>
      </div>
      <div className="form-section">
        <Form.Group controlId="lcl_documentation_closing_cutoff_offset_days">
          <Form.Label>Documentation Closing Offset Days</Form.Label>
          <Form.Control
            type="number"
            value={editRoute.lcl_documentation_closing_cutoff_offset_days || 0}
            onChange={(e) =>
              setEditRoute({ ...editRoute, lcl_documentation_closing_cutoff_offset_days: parseInt(e.target.value, 10) || 0 })
            }
          />
          <small>The number of days to offset from the LCL schedule provider's POL cutoff date for Documentation</small>
        </Form.Group>
      </div>
      <Button variant="primary" onClick={handleSaveEditRoute}>
        Save
      </Button>
      <Button variant="secondary" onClick={handleCancel}>
        Cancel
      </Button>
    </div>
  );
};

export default EditRouteForm;
